import { useCallback, useMemo, useState } from 'react';

import type { Webhook } from 'api';

import { usePermissions } from 'hooks/usePermissions';

import { DataTable } from 'components/ui/organisms/DataTable';

import { getColumns } from './columns';
import {
  WEBHOOKS_ROW_TEST_ID,
  WEBHOOKS_TABLE_TEST_ID,
  WEBHOOKS_TOTAL_TEST_ID
} from './constants';
import { EditWebhookForm, WebhookDetails } from './Details';
import { SelectionBar } from './SelectionBar';

import { useWebhooks } from '../hooks/useWebhooks';

type WebhooksTableProps = {
  selectedUuids: Set<string>;
  onToggleRow: (uuid: string) => void;
  onToggleAll: () => void;
  onActivateAll: () => void;
  onDeactivateAll: () => void;
  onBulkEdit: () => void;
};

export const WebhooksTable = ({
  selectedUuids,
  onToggleRow,
  onToggleAll,
  onActivateAll,
  onDeactivateAll,
  onBulkEdit
}: WebhooksTableProps) => {
  const { webhooks } = useWebhooks();

  const editableCount = useMemo(
    () => webhooks.filter((webhook) => webhook.editable).length,
    [webhooks]
  );
  const allSelected = editableCount > 0 && selectedUuids.size === editableCount;
  const someSelected = selectedUuids.size > 0 && !allSelected;
  const hasSelection = selectedUuids.size >= 1;

  const { isPermitted } = usePermissions();
  const canManage = isPermitted('developers:manage');

  const columns = useMemo(
    () =>
      getColumns({
        canManage,
        selectedUuids,
        allSelected,
        someSelected,
        onToggleRow,
        onToggleAll
      }),
    [
      canManage,
      selectedUuids,
      allSelected,
      someSelected,
      onToggleRow,
      onToggleAll
    ]
  );

  const [selectedUuid, setSelectedUuid] = useState<string>();
  const [isEditing, setIsEditing] = useState(false);
  const selectedWebhook = useMemo(
    () => webhooks.find((webhook) => webhook.uuid === selectedUuid),
    [webhooks, selectedUuid]
  );
  const handleRowClick = useCallback((record: Webhook) => {
    setIsEditing(false);
    setSelectedUuid((current) =>
      current === record.uuid ? undefined : record.uuid
    );
  }, []);
  const closePanel = useCallback(() => {
    setIsEditing(false);
    setSelectedUuid(undefined);
  }, []);

  return (
    <div className="flex flex-1 flex-col pb-8">
      <DataTable<Webhook>
        total={hasSelection ? undefined : webhooks.length}
        totalDataTestId={WEBHOOKS_TOTAL_TEST_ID}
        headerClassName="min-h-[90px]"
        tableActionsClassName={hasSelection ? 'flex-1' : undefined}
        tableActions={
          hasSelection ? (
            <SelectionBar
              count={selectedUuids.size}
              onActivateAll={onActivateAll}
              onDeactivateAll={onDeactivateAll}
              onBulkEdit={onBulkEdit}
            />
          ) : undefined
        }
        dataSource={webhooks}
        columns={columns}
        rowKey="uuid"
        onRowClick={handleRowClick}
        selectedRow={selectedWebhook}
        tableDataTestId={WEBHOOKS_TABLE_TEST_ID}
        rowDataTestId={WEBHOOKS_ROW_TEST_ID}
        rightPanel={
          selectedWebhook ? (
            isEditing ? (
              <EditWebhookForm
                key={selectedWebhook.uuid}
                webhook={selectedWebhook}
                onClose={closePanel}
                onCancel={() => setIsEditing(false)}
                onSaved={() => setIsEditing(false)}
              />
            ) : (
              <WebhookDetails
                key={selectedWebhook.uuid}
                webhook={selectedWebhook}
                onClose={closePanel}
                onEdit={() => setIsEditing(true)}
              />
            )
          ) : undefined
        }
      />
    </div>
  );
};
